import React from 'react';
import classNames from 'classnames';

import { useLocale } from '../../context/localeContext';
import { useIntl } from '../../util/reactIntl';

import { Menu, MenuLabel, MenuContent, MenuItem, InlineTextButton } from '../../components';

import css from './LanguageSwitcher.module.css';

const SUPPORTED_LANGUAGES = [
  { code: 'de', flag: '\u{1F1E9}\u{1F1EA}', labelKey: 'LanguageSwitcher.de' },
  { code: 'en', flag: '\u{1F1EC}\u{1F1E7}', labelKey: 'LanguageSwitcher.en' },
  { code: 'es', flag: '\u{1F1EA}\u{1F1F8}', labelKey: 'LanguageSwitcher.es' },
  { code: 'fr', flag: '\u{1F1EB}\u{1F1F7}', labelKey: 'LanguageSwitcher.fr' },
];

/**
 * Language switcher dropdown that allows users to change the UI language.
 * Uses LocaleContext to read and update the active locale.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className - additional CSS class
 * @param {string?} props.rootClassName - overwrite root CSS class
 * @returns {JSX.Element}
 */
const LanguageSwitcher = props => {
  const { className, rootClassName } = props;
  const { locale, setLocale } = useLocale();
  const intl = useIntl();

  const currentLang = SUPPORTED_LANGUAGES.find(l => l.code === locale) || SUPPORTED_LANGUAGES[0];
  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes}>
      <Menu contentPosition="left">
        {/* WCAG 2.5.3 Label in Name: the accessible name must contain the
            visible text (the language code shown on the button). */}
        <MenuLabel
          className={css.menuLabel}
          isOpenClassName={css.menuLabelOpen}
          ariaLabel={`${currentLang.code.toUpperCase()} – ${intl.formatMessage({
            id: 'LanguageSwitcher.label',
          })}`}
        >
          {/* A globe, not the flag+code pair this used to show. Two reasons: the round 40px icon
              button has no room for a text label, and a national flag is the wrong symbol for a
              language — a German flag standing for the German of a Swiss marketplace was actively
              odd. The active language stays available to assistive tech via ariaLabel above, and
              is marked in the open menu.
              `fill: none` is set on the container in CSS: the app-wide `svg { fill: currentColor }`
              rule would otherwise turn this stroke icon into a solid black disc. */}
          <span className={css.globeIcon}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <circle cx="9" cy="9" r="7.25" stroke="currentColor" strokeWidth="1.5" />
              <path
                d="M1.75 9h14.5M9 1.75c1.9 2 2.9 4.6 2.9 7.25S10.9 14.25 9 16.25c-1.9-2-2.9-4.6-2.9-7.25S7.1 3.75 9 1.75Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </MenuLabel>
        <MenuContent className={css.menuContent}>
          {SUPPORTED_LANGUAGES.map(lang => {
            const isActive = lang.code === locale;
            return (
              <MenuItem key={lang.code}>
                <InlineTextButton
                  rootClassName={classNames(css.menuItem, {
                    [css.menuItemActive]: isActive,
                  })}
                  onClick={() => setLocale(lang.code)}
                >
                  <span className={css.menuItemBorder} />
                  <span className={css.menuItemFlag}>{lang.flag}</span>
                  <span className={css.menuItemLabel}>
                    {intl.formatMessage({ id: lang.labelKey })}
                  </span>
                </InlineTextButton>
              </MenuItem>
            );
          })}
        </MenuContent>
      </Menu>
    </div>
  );
};

export default LanguageSwitcher;
